import { WS_URL } from './config.js';

export class SignalingClient {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.intentionalClose = false;
    this.connecting = null;
  }

  isOpen() {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  connect(sessionId) {
    this.sessionId = sessionId;

    if (this.isOpen()) {
      return Promise.resolve();
    }

    if (this.connecting) return this.connecting;

    this.closeSocket({ emit: false });

    this.connecting = new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.connecting = null;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('WebSocket closed');
          const emit = !this.intentionalClose;
          this.intentionalClose = false;
          this.ws = null;
          this.connecting = null;
          if (emit) this.emit('close');
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.connecting = null;
          reject(error);
        };
      } catch (error) {
        this.connecting = null;
        reject(error);
      }
    });

    return this.connecting;
  }

  handleMessage(message) {
    const { type } = message;
    this.emit(type, message);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket not connected');
    }
  }

  joinQueue(clientKey) {
    if (clientKey) this.clientKey = clientKey;
    this.send({
      type: 'JOIN_QUEUE',
      sessionId: this.sessionId,
      clientKey: clientKey || this.clientKey,
    });
  }

  report(reason, note) {
    this.send({
      type: 'REPORT',
      reason,
      note,
    });
  }

  leave() {
    this.send({ type: 'LEAVE' });
    this.close();
  }

  leaveQueue() {
    this.send({ type: 'LEAVE' });
  }

  // Leave current session but stay connected (for finding new match while waiting)
  leaveSession() {
    this.send({ type: 'FIND_NEW_MATCH' });
    // Don't close the WebSocket - keep it open
  }

  rejoinQueue() {
    // Rejoin queue with same sessionId (for reconnection)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        type: 'JOIN_QUEUE',
        sessionId: this.sessionId,
        clientKey: this.clientKey,
      });
    }
  }

  sendOffer(offer) {
    this.send({
      type: 'OFFER',
      data: offer,
    });
  }

  sendAnswer(answer) {
    this.send({
      type: 'ANSWER',
      data: answer,
    });
  }

  sendIceCandidate(candidate) {
    this.send({
      type: 'ICE_CANDIDATE',
      data: candidate,
    });
  }

  reconnect() {
    this.send({
      type: 'RECONNECT',
      sessionId: this.sessionId,
    });
  }

  cancelReconnect() {
    this.send({
      type: 'CANCEL_RECONNECT',
      sessionId: this.sessionId,
    });
  }

  close() {
    this.closeSocket({ emit: false });
  }

  closeSocket({ emit } = { emit: false }) {
    if (!this.ws) return;

    this.intentionalClose = !emit;
    const socket = this.ws;
    this.ws = null;

    if (!emit) {
      socket.onclose = null;
      socket.onerror = null;
    }

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }
}
